import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { usePostUsermanagerChangepwdMutation } from '@/api'
import {
  ConfirmPassword,
  Form,
  FormError,
  OperationButton,
  SomethingWentWrongFormError
} from '@/components/core-v2'
import {
  ConfirmPasswordField,
  NewPassword,
  NewPasswordField,
  OldPassword,
  OldPasswordField
} from '@/components/core-v2/molecules/fields/password'
import {
  alphanumeric,
  containsUpperCaseLetter,
  minimumAmountOfNumbers,
  minimumCharacterLength,
  validatePassword
} from '@/components/core-v2/molecules/fields/validators'
import { useUser } from '@/utilities/hooks/useUser'

import { ValidationRule } from '../../validation-rule-block'
import { COPY } from '../set-up-password/constants'
import { BaseFormProps } from '../types'
import classes from './index.module.scss'
import { Success } from './Success'

const validators = {
  validate: {
    ...minimumCharacterLength(8).validate,
    ...containsUpperCaseLetter.validate,
    ...minimumAmountOfNumbers(2).validate,
    ...alphanumeric.validate
  }
}

export type ChangePasswordFields = OldPasswordField & NewPasswordField & ConfirmPasswordField

const { validation } = COPY

export const ChangePasswordForm = ({ onSuccess }: Partial<BaseFormProps>) => {
  const { data: user } = useUser()
  const form = useForm<ChangePasswordFields>()

  const [postChangePassword, { error, isError, isLoading, isSuccess }] =
    usePostUsermanagerChangepwdMutation()

  const is401Error = isError && 'status' in error && error.status === 401
  const isServerError = isError && !is401Error

  const onSubmit = async (values: ChangePasswordFields) => {
    postChangePassword({
      changePasswordCommand: {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        userName: user.userName
      }
    })
  }

  useEffect(() => {
    if (isSuccess) onSuccess?.()
  }, [isSuccess, onSuccess])

  const [newPasswordValue, confirmPasswordValue] = form.watch(['newPassword', 'confirmPassword'])
  const arePasswordsMatching = newPasswordValue === confirmPasswordValue
  const confirmPasswordValidator = {
    validate: () => arePasswordsMatching || 'Passwords do not match'
  }
  const { hasMinimumCharacters, hasOneUppercaseLetter, hasTwoNumbers, isAlphanumeric } =
    validatePassword(newPasswordValue)

  if (isSuccess) return <Success />

  return (
    <div>
      <FormProvider {...form}>
        <Form
          noValidate
          aria-busy={isLoading}
          name="Change password form"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <OldPassword isDisabled={isLoading} />
          <NewPassword
            aria-describedby="password-validations"
            isDisabled={isLoading}
            validators={validators}
          />
          <ConfirmPassword
            aria-describedby="password-validations"
            isDisabled={isLoading}
            validators={confirmPasswordValidator}
          />
          <span className={classes.validationHeading}>{validation.heading}</span>
          <ValidationRule.Block id="password-validations">
            <ValidationRule.Rule isCorrect={hasMinimumCharacters} label={validation.characters} />
            <ValidationRule.Rule isCorrect={hasOneUppercaseLetter} label={validation.upperCase} />
            <ValidationRule.Rule isCorrect={hasTwoNumbers} label={validation.numbers} />
            <ValidationRule.Rule isCorrect={isAlphanumeric} label={validation.specialSymbols} />
          </ValidationRule.Block>
          <FormError className="mt-6 mb-5" isShowing={is401Error} title="Error">
            Invalid old password
          </FormError>
          <SomethingWentWrongFormError className="mt-6 mb-5" isShowing={isServerError} />
          <OperationButton
            className="mt-6"
            isDisabled={isLoading}
            isLoading={isLoading}
            operationType="progress"
            type="submit"
            width="100%"
          >
            Change password
          </OperationButton>
        </Form>
      </FormProvider>
    </div>
  )
}
